import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, brands as brandsApi } from '@/utils/api';
import { Zap, ArrowRight, ArrowLeft, CheckCircle2, Sparkles, Building2 } from 'lucide-react';

const PROJECT_TYPES = [
  'AI Chatbot',
  'Workflow Automation',
  'Website / Marketing Site',
  'Custom Web App',
  'Data Pipeline / Integration',
  'Something else',
];
const BUDGETS = ['< $5k', '$5-15k', '$15-50k', '$50k+', 'Not sure yet'];

export default function Signup() {
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState({
    name: '', email: '', password: '', company_name: '',
    brand_id: '',
    project_name: '', project_type: PROJECT_TYPES[0],
    description: '', budget_range: BUDGETS[1],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    brandsApi.list().then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const canNext1 = form.name && form.email && form.password && form.company_name;
  const canSubmit = canNext1 && form.project_name && form.description;

  const submit = async () => {
    setSubmitting(true);
    setErr('');
    try {
      await auth.signupWithProject(form);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to sign up');
    } finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
      <div className="max-w-md w-full card-base p-8 text-center" data-testid="signup-success">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#3C8A5A20' }}>
          <CheckCircle2 size={26} style={{ color: '#3C8A5A' }} />
        </div>
        <h2 className="font-black text-xl mb-2" style={{ color: 'var(--ink)' }}>Thanks, {form.name.split(' ')[0]}!</h2>
        <p className="text-sm mb-5" style={{ color: 'var(--muted)' }}>
          Your account is pending approval. We'll review your project and get back to you shortly. Once approved you can log in with the email + password you chose.
        </p>
        <Link to="/login" className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2">
          Go to sign in <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex font-sans" style={{ background: 'var(--bg)' }}>
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 relative" style={{ background: '#0D1113' }}>
        <div className="relative z-10 absolute top-8 left-12">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#006080' }}>
              <Zap size={18} color="white" strokeWidth={2.5} />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">Krris OS</span>
          </div>
        </div>
        <div className="relative z-10">
          <h2 className="text-4xl font-black text-white leading-tight mb-4">
            Start your project.<br />One form.
          </h2>
          <p className="text-base leading-relaxed" style={{ color: '#8A99A0' }}>
            Tell us about your idea — the moment you're approved, your portal comes alive with chat, bug reports, invoices, and real progress tracking.
          </p>
          <div className="mt-8 space-y-2">
            {['Real-time chat with your PM', 'Every project deliverable in one place', 'Transparent pricing + milestones'].map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" style={{ color: '#D9E9EE' }}>
                <CheckCircle2 size={14} style={{ color: '#006080' }} /> {t}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#006080' }}>Step {step} of 2</span>
            <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
              <div className="h-full transition-all" style={{ width: `${step === 1 ? 50 : 100}%`, background: '#006080' }} />
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-3" data-testid="signup-step-1">
              <h1 className="text-2xl font-black mb-1" style={{ color: 'var(--ink)' }}>Create your account</h1>
              <p className="text-sm mb-5" style={{ color: 'var(--muted)' }}>You choose your password now — we won't email you a temp one.</p>

              <div>
                <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Name</label>
                <input className="input-agency text-sm" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="signup-name" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Work Email</label>
                <input className="input-agency text-sm" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="signup-email" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Password</label>
                <input className="input-agency text-sm" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="signup-password" />
                <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Choose something strong — at least 8 chars.</p>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Company</label>
                <input className="input-agency text-sm" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="signup-company" />
              </div>
              {brands.length > 1 && (
                <div>
                  <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Which brand will you work with?</label>
                  <select className="input-agency text-sm" value={form.brand_id} onChange={e => setForm({...form, brand_id: e.target.value})}>
                    <option value="">Not sure / default</option>
                    {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
              <button disabled={!canNext1} onClick={() => setStep(2)} className="btn-primary w-full py-2.5 flex items-center justify-center gap-2 text-sm disabled:opacity-50" data-testid="signup-next">
                Next — tell us about your project <ArrowRight size={14} />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="signup-step-2">
              <h1 className="text-2xl font-black mb-1" style={{ color: 'var(--ink)' }}>Your first project</h1>
              <p className="text-sm mb-5" style={{ color: 'var(--muted)' }}>Rough strokes only — we'll refine together.</p>

              <div>
                <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Project name</label>
                <input className="input-agency text-sm" value={form.project_name} onChange={e => setForm({...form, project_name: e.target.value})} placeholder="e.g. Acme AI support bot" data-testid="signup-project-name" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>What are you building?</label>
                <select className="input-agency text-sm" value={form.project_type} onChange={e => setForm({...form, project_type: e.target.value})} data-testid="signup-project-type">
                  {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Budget range</label>
                <select className="input-agency text-sm" value={form.budget_range} onChange={e => setForm({...form, budget_range: e.target.value})}>
                  {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>The gist</label>
                <textarea className="input-agency text-sm resize-none" rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What problem are you solving? Who's it for? Any must-haves or timing constraints?" data-testid="signup-description" />
              </div>

              {err && <div className="text-xs p-2 rounded-btn" style={{ background: '#B34A3A15', color: '#B34A3A' }} data-testid="signup-error">{err}</div>}

              <div className="flex gap-2">
                <button onClick={() => setStep(1)} className="flex-1 py-2.5 rounded-btn text-sm flex items-center justify-center gap-1.5" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                  <ArrowLeft size={14} /> Back
                </button>
                <button disabled={!canSubmit || submitting} onClick={submit} className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2 text-sm disabled:opacity-50" data-testid="signup-submit">
                  {submitting ? 'Submitting…' : <>Submit for review <Sparkles size={14} /></>}
                </button>
              </div>
            </div>
          )}

          <div className="text-xs text-center mt-6" style={{ color: 'var(--muted)' }}>
            Already have an account? <Link to="/login" className="font-semibold" style={{ color: '#006080' }}>Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
